import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import {
  Checkbox,
  TextInput,
  Select,
  SelectItem,
} from 'carbon-components-react';
import { useClassList } from '../../../../api/class.service';
import StageIndicator from '../stage-indicator/StageIndicator';
import {
  getQueryString,
  useReducerSpread,
} from '../../../../utils/common.util';
import CustomButton from '../../../../components/custom-button/custom-btn.component';
import { removeStudentFromProgram } from '../financial-aid.service';
import './../financial-aid.style.scss';

export default function AssignStudentsToAid({
  program,
  onFinish,
  onBack,
  initialData,
  isLoading: isLoadingProp = false,
  isEditMode = false,
  programId = null,
  classFilterFromStep2 = null,
  linkedData = null,
}) {
  const { allClasses } = useClassList();

  // Determine the restricted class - if classFilterFromStep2 is set, only show that class
  const restrictedClassId = classFilterFromStep2
    ? classFilterFromStep2.toString()
    : null;

  // Filter classes: if restricted, show only that class; otherwise show all
  const displayedClasses = restrictedClassId
    ? allClasses?.filter((cls) => cls.id.toString() === restrictedClassId) || []
    : allClasses || [];

  const defaultFormData = {
    assignedStudents: [],
    overrideAmounts: {},
    search: '',
    classId:
      restrictedClassId ||
      (allClasses && allClasses.length > 0 ? allClasses[0].id : ''),
    selectedAvailableStudents: [],
  };

  // Safely merge initialData with defaultFormData to ensure all required fields exist
  const initialFormData = initialData
    ? {
        ...defaultFormData,
        ...initialData,
        assignedStudents: initialData.assignedStudents || [],
        overrideAmounts: initialData.overrideAmounts || {},
      }
    : defaultFormData;

  const [formData, setFormData] = useReducerSpread(initialFormData);

  // Error state for student removal in edit mode
  const [removalError, setRemovalError] = useState(null);
  const [removingStudentId, setRemovingStudentId] = useState(null);

  // Update form data when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData && Array.isArray(initialData.assignedStudents)) {
      setFormData({
        ...initialData,
        assignedStudents: initialData.assignedStudents || [],
        overrideAmounts: initialData.overrideAmounts || {},
      });
    }
  }, [initialData]);

  // Determine the default classId for fetching students:
  // - If restrictedClassId is set (from Step 2), use that
  // - Otherwise use first class or form selection
  const defaultClassId =
    restrictedClassId ||
    (allClasses && allClasses.length > 0 ? allClasses[0].id : '');

  const { data: students, isLoading: isFetchingStudents } = useSWR(
    `student/list/?${getQueryString({
      classId: formData.classId || defaultClassId,
      query: formData.search,
      status: 'Enrolled',
    })}`,
  );

  const { data: billItems } = useSWR('billing/fees/');

  const linkedBillItemIds = linkedData?.selectedBillItems || [];
  const linkedBillItems =
    billItems?.filter((item) => linkedBillItemIds.includes(item.id)) || [];
  const totalLinkedFeeAmount = linkedBillItems.reduce(
    (sum, item) => sum + (item.amount || 0),
    0,
  );

  const availableStudents =
    students?.filter(
      (student) =>
        !(formData.assignedStudents || []).some(
          (assigned) =>
            // Compare using numeric id - assigned students have studentId (numeric),
            // available students have id (numeric)
            String(assigned.studentId || assigned.id) === String(student.id),
        ),
    ) || [];

  // a simple function to Show placeholder based on amount type
  const getPlaceholder = () => {
    if (program.amountType === 'Percentage') {
      return `${program.amount}% — default`;
    }
    return `GHS ${program.amount} — default`;
  };

  const handleAvailableStudentCheckboxChange = (studentId, isChecked) => {
    let updatedSelected = formData.selectedAvailableStudents;
    if (isChecked) {
      updatedSelected = [...updatedSelected, studentId];
    } else {
      updatedSelected = updatedSelected.filter((id) => id !== studentId);
    }
    setFormData({ selectedAvailableStudents: updatedSelected });
  };

  const handleSelectAllAvailable = (isChecked) => {
    if (isChecked) {
      setFormData({
        selectedAvailableStudents: availableStudents.map((s) => s.id),
      });
    } else {
      setFormData({ selectedAvailableStudents: [] });
    }
  };

  const handleAssignSelectedStudents = () => {
    const studentsToAssign = availableStudents
      .filter((student) =>
        formData.selectedAvailableStudents.includes(student.id),
      )
      .map((student) => ({
        // Use numeric id as studentId for API compatibility
        id: student.id?.toString(),
        studentId: student.id?.toString(),
        fullName: student.name || student.fullName || student.studentName,
      }));
    setFormData({
      assignedStudents: [
        ...(formData.assignedStudents || []),
        ...studentsToAssign,
      ],
      selectedAvailableStudents: [],
    });
  };

  const handleRemoveStudent = async (studentId) => {
    // Clear any previous errors
    setRemovalError(null);

    // In edit mode, determine if student has been persisted to API
    const isPersistedStudent = initialData?.assignedStudents?.some(
      (s) => String(s.id || s.studentId) === String(studentId),
    );

    // In edit mode with a persisted student, call the API to remove
    if (isEditMode && programId && isPersistedStudent) {
      setRemovingStudentId(studentId);
      try {
        const result = await removeStudentFromProgram(programId, studentId);
        if (!result.success) {
          setRemovalError(
            result.message || 'Failed to remove student from program',
          );
          setRemovingStudentId(null);
          return;
        }
        const { [studentId]: removed, ...remainingOverrides } =
          formData.overrideAmounts;
        setFormData({
          assignedStudents: (formData.assignedStudents || []).filter(
            (s) => s.id !== studentId,
          ),
          overrideAmounts: remainingOverrides,
        });
      } catch (error) {
        setRemovalError(
          error.message || 'An error occurred while removing student',
        );
      } finally {
        setRemovingStudentId(null);
      }
    } else {
      // Either create mode OR edit mode with a non-persisted student (newly added)
      // Remove locally without API call
      const { [studentId]: removed, ...remainingOverrides } =
        formData.overrideAmounts;
      setFormData({
        assignedStudents: (formData.assignedStudents || []).filter(
          (s) => s.id !== studentId,
        ),
        overrideAmounts: remainingOverrides,
      });
    }
  };

  const handleOverrideAmountChange = (studentId, value) => {
    // Validate based on amount type from program
    if (program.amountType === 'Percentage') {
      // Allow only numbers between 0 and 100 for percentage
      if (value === '' || (Number(value) >= 0 && Number(value) <= 100)) {
        setFormData({
          overrideAmounts: {
            ...formData.overrideAmounts,
            [studentId]: value,
          },
        });
      }
    } else {
      // For fixed amount, allow non-negative number up to total linked fee amount
      const numValue = Number(value);
      if (value === '' || (numValue >= 0 && numValue <= totalLinkedFeeAmount)) {
        setFormData({
          overrideAmounts: {
            ...formData.overrideAmounts,
            [studentId]: value,
          },
        });
      }
    }
  };

  const isValidForm = () => {
    // At least one student must be assigned
    if ((formData.assignedStudents || []).length === 0) {
      console.warn('Please assign at least one student');
      return false;
    }

    // Override amounts must be valid based on amount type
    for (const [studentId, amount] of Object.entries(
      formData.overrideAmounts,
    )) {
      if (amount === '') continue;

      if (isNaN(amount)) {
        console.warn(
          `Invalid amount for student ${studentId}: must be a number`,
        );
        return false;
      }
      const numAmount = parseFloat(amount);
      if (numAmount < 0) {
        console.warn(
          `Invalid amount for student ${studentId}: must be non-negative`,
        );
        return false;
      }
      if (
        program.amountType !== 'Percentage' &&
        numAmount > totalLinkedFeeAmount
      ) {
        console.warn(
          `Invalid amount for student ${studentId}: cannot exceed total linked fee amount (${totalLinkedFeeAmount})`,
        );
        return false;
      }
      if (program.amountType === 'Percentage' && numAmount > 100) {
        console.warn(
          `Invalid amount for student ${studentId}: percentage cannot exceed 100`,
        );
        return false;
      }
    }

    return true;
  };

  const handleFinish = () => {
    if (isValidForm()) {
      onFinish({
        assignedStudents: formData.assignedStudents,
        overrideAmounts: formData.overrideAmounts,
      });
    }
  };

  return (
    <div
      className="d-flex-center"
      style={{ maxWidth: '1200px', margin: '0 auto' }}
    >
      <div
        style={{
          display: 'grid',
          gap: '20px',
          width: '100%',
          marginBottom: '30px',
        }}
      >
        <h2>{program.programName} — Assign Students</h2>
        <p>
          Select students this program should apply to and optionally set a
          different amount per student.
        </p>
        <StageIndicator currentStage={3} />

        {/* Filters */}
        <div className="students-filters d-flex" style={{ gap: '20px' }}>
          <div style={{ flex: 1 }}>
            <TextInput
              labelText="Search Students"
              placeholder="Search by name or student ID"
              value={formData.search}
              onChange={(e) => setFormData({ search: e.target.value })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Select
              labelText="Filter by Class"
              value={formData.classId}
              onChange={(e) => setFormData({ classId: e.target.value })}
            >
              {/* <SelectItem value="" text="All Classes" /> */}
              {displayedClasses.map((cls) => (
                <SelectItem
                  key={cls.id}
                  value={cls.id}
                  text={cls.description}
                />
              ))}
            </Select>
          </div>
        </div>

        {/* Three Column Layout: Available | Assign Button | Assigned */}
        <div className="students-assignment-layout">
          {/* Available Students */}
          <div
            className="students-column"
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              padding: '16px',
              maxHeight: '500px',
              overflowY: 'auto',
            }}
          >
            <div
              className="d-flex-space-between"
              style={{
                marginBottom: '16px',
                maxHeight: '500px',
                overflowY: 'auto',
              }}
            >
              <h3>Available Students ({availableStudents.length})</h3>
              {availableStudents.length > 0 && (
                <div>
                  <Checkbox
                    id="select-all-available"
                    labelText="Select All"
                    checked={
                      availableStudents.length > 0 &&
                      formData.selectedAvailableStudents.length ===
                        availableStudents.length
                    }
                    onChange={handleSelectAllAvailable}
                  />
                </div>
              )}
            </div>
            {isFetchingStudents && <p>Loading students...</p>}
            {!isFetchingStudents && availableStudents.length > 0 && (
              <div className="available-students-scroll">
                {availableStudents.map((student) => (
                  <div
                    key={student.id}
                    className="d-flex-space-between"
                    style={{
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                      }}
                    >
                      <div>
                        <Checkbox
                          id={`available-${student.id}`}
                          checked={formData.selectedAvailableStudents.includes(
                            student.id,
                          )}
                          onChange={(isChecked) =>
                            handleAvailableStudentCheckboxChange(
                              student.id,
                              isChecked,
                            )
                          }
                        />
                      </div>
                      <div style={{ marginLeft: '16px' }}>
                        <strong>{student.fullName}</strong>
                        <p
                          style={{
                            margin: '4px 0 0 0',
                            color: '#666',
                            fontSize: '14px',
                          }}
                        >
                          ID: {student.studentId}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!isFetchingStudents && availableStudents.length === 0 && (
              <p>No students available.</p>
            )}
          </div>

          {/* Assign Button */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CustomButton
              classes="primary"
              onClick={handleAssignSelectedStudents}
              disabled={formData.selectedAvailableStudents.length === 0}
            >
              Assign →<br />({formData.selectedAvailableStudents.length})
            </CustomButton>
          </div>

          {/* Assigned Students */}
          <div
            className="students-column"
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              padding: '16px',
              maxHeight: '500px',
              overflowY: 'auto',
            }}
          >
            <h3>
              Assigned Students ({(formData.assignedStudents || []).length})
            </h3>
            <p className="text-12">
              Enter an <span className="font-bold">optional</span> override
              amount for each selected student.
            </p>

            {/* Error display for student removal */}
            {removalError && (
              <p className="text-14 mt-10 text-danger">{removalError}</p>
            )}

            {(formData.assignedStudents || []).length > 0 && (
              <div className="assigned-students-scroll">
                {(formData.assignedStudents || []).map((student) => (
                  <div
                    key={student.id}
                    className="d-flex-space-between student-assigned-row"
                    style={{
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <strong>
                        {student.fullName}{' '}
                        {student.class && `- ${student.class}`}
                      </strong>
                      <p
                        style={{
                          margin: '4px 0 0 0',
                          color: '#666',
                          fontSize: '14px',
                        }}
                      >
                        ID: {student.studentUniqueId}
                      </p>
                    </div>
                    <div
                      className="student-override-controls"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <TextInput
                        size="sm"
                        type="number"
                        value={formData.overrideAmounts[student.id] || ''}
                        onChange={(e) =>
                          handleOverrideAmountChange(student.id, e.target.value)
                        }
                        placeholder={getPlaceholder()}
                        style={{ width: '120px' }}
                      />
                      <CustomButton
                        classes="outline no-hover font-bold secondary"
                        onClick={() => handleRemoveStudent(student.id)}
                        style={{ fontSize: '12px', padding: '6px 12px' }}
                        isLoading={removingStudentId === student.id}
                        disabled={removingStudentId === student.id}
                      >
                        Remove
                      </CustomButton>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(formData.assignedStudents || []).length === 0 && (
              <p>No students assigned yet.</p>
            )}
          </div>
        </div>

        <div className="mt-40 d-flex-space-between">
          <CustomButton
            classes="outline no-hover font-bold secondary"
            onClick={() => onBack(formData)}
            disabled={isLoadingProp}
          >
            Back
          </CustomButton>
          <CustomButton
            classes="font-bold primary"
            onClick={handleFinish}
            disabled={!isValidForm() || isLoadingProp}
            isLoading={isLoadingProp}
          >
            Finish
          </CustomButton>
        </div>
      </div>
    </div>
  );
}
